import { TransitionScreen } from '@components/transition';
import useKeycloak from '@hooks/UseKeycloak';
import { useTheme } from '@mui/material';
import Keycloak from 'keycloak-js';
import { startsWith } from 'lodash';
import posthog from 'posthog-js';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryParam } from 'use-query-params';

interface AuthContextType {
  loading: boolean;
  error?: Error;
  user?: KeycloakUser;
  authenticated: boolean;
  token?: string;
  logout: () => void;
  register: () => void;
}

export type KeycloakUser = Keycloak['tokenParsed'] & {
  preferred_username: string;
  email: string;
  name: string;
};

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const {
    palette: { mode },
  } = useTheme();

  const [registrationComplete] = useQueryParam<string>('registration_complete');
  const [user, setUser] = useState<KeycloakUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error>();
  const [authenticated, setAuthenticated] = useState(false);
  const [authClient, setAuthClient] = useState<Keycloak>();

  const { keycloak, loading: keycloakLoading } = useKeycloak();

  const logout = useMemo(() => {
    return () => {
      setLoading(true);
      posthog.capture('user_logout_intent');
      posthog.reset();
      setUser(null);
      void authClient?.logout();
    };
  }, [authClient]);

  const register = useMemo(() => {
    return () => {
      setLoading(true);
      posthog.capture('user_registration_intent');
      void authClient?.register({
        redirectUri:
          typeof window !== 'undefined'
            ? `${window.location.origin}?registration_complete=true`
            : undefined,
      });
    };
  }, [authClient]);

  const refreshToken = useCallback(async () => {
    try {
      if (!authClient) return;
      const refreshed = await authClient.updateToken(8 * 60 * 60);
      if (refreshed) {
        setUser(authClient.tokenParsed as KeycloakUser);
        setAuthenticated(true);
      }
    } catch (err) {
      console.error('Failed to refresh token:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh token'));
      setAuthenticated(false);
      logout();
    }
  }, [authClient, logout]);

  useEffect(() => {
    if (!keycloak || keycloakLoading) {
      return;
    }

    if (keycloak.sessionId) {
      setLoading(false);
      setAuthenticated(keycloak.authenticated);
      setAuthClient(keycloak);
      setUser(keycloak.tokenParsed as KeycloakUser);
      return;
    }

    keycloak.onReady = () => {
      setLoading(false);
    };
    keycloak.onAuthSuccess = () => {
      setAuthenticated(true);
      posthog.capture('user_login_complete');
      posthog.capture('user_login');
      setUser(keycloak.tokenParsed as KeycloakUser);
    };
    keycloak.onAuthError = (errorData) => {
      setAuthenticated(false);
      posthog.capture('user_login_failed', { error: errorData });
      setLoading(false);
      setUser(null);
    };
    keycloak.onAuthLogout = () => {
      setAuthenticated(false);
      posthog.capture('user_logout_complete');
      setLoading(false);
      setUser(null);
    };
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(5).catch((e) => console.error('Failed to refresh token', e));
    };
    keycloak.onAuthRefreshSuccess = () => setAuthenticated(true);
    keycloak.onAuthRefreshError = () => {
      setAuthenticated(false);
      posthog.capture('user_token_refresh_failed');
    };

    const isRegisterPage =
      typeof window !== 'undefined' && startsWith(window.location.pathname, '/register');

    const defaultCreateLoginUrl = keycloak.createLoginUrl.bind(
      keycloak,
    ) as Keycloak['createLoginUrl'];

    keycloak.createLoginUrl = (options) => {
      return defaultCreateLoginUrl(options).then((url) => {
        return `${url}&theme=${mode}`;
      });
    };

    keycloak
      .init({
        onLoad: isRegisterPage ? 'check-sso' : 'login-required',
        enableLogging: true,
        checkLoginIframe: false,
        pkceMethod: 'S256',
        adapter: 'default',
        responseMode: 'fragment',
      })
      .catch((e: Error) => {
        console.error('Failed to initialize Keycloak:', e?.message || 'Unknown error');
      });

    setAuthClient(keycloak);
  }, [keycloakLoading, keycloak]);

  useEffect(() => {
    if (!user) {
      return;
    }
    posthog.identify(user.preferred_username, { email: user.email });
    if (registrationComplete) {
      posthog.capture('user_registration_complete');
    }
  }, [user]);

  useEffect(() => {
    if (!authenticated || !refreshToken) return () => {};

    const refreshInterval = setInterval(
      () => {
        void refreshToken();
      },
      60 * 60 * 1000,
    );

    return () => clearInterval(refreshInterval);
  }, [authenticated, refreshToken]);

  const value = useMemo(
    () => ({
      loading,
      error,
      user,
      authenticated,
      token: authClient?.token,
      logout,
      register,
    }),
    [loading, user, authenticated, authClient?.token, logout, register],
  );

  if (loading) {
    return <TransitionScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
